// Module: FlowScreen
// Role: Root orchestrator for /flow/:slotId route. Selects template based on
//       flow state machine (pending/rejected/accepted/completed).
// Uses: flow-client, templates, PlatformProvider (back btn, capabilities)
// Used by: App.tsx route /flow/:slotId
//
// MOCK STRATEGY (L-220): VITE_FLOW_MOCK constant-folded by Vite; mock module
// tree-shaken in prod (sideEffects: false assumed).

import { useContext, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as realClient from '../../api/flow-client';
import * as mockClient from '../../api/flow-client.mock';
import type { GetFlowResponse, CheckpointAttempt } from '../../api/flow-types';
import { InstructionScreen } from './templates/InstructionScreen';
import { AwaitingVerdict } from './templates/AwaitingVerdict';
import { RejectScreen } from './templates/RejectScreen';
import { TerminalSheet } from './templates/TerminalSheet';
import { ModerationStatus } from './templates/ModerationStatus';
import { CartScreen } from './templates/CartScreen';
import { S04SuccessContinue } from './templates/S04SuccessContinue';
import { PickupOverviewScreen } from './templates/PickupOverviewScreen';
import { UgcOverviewScreen } from './templates/UgcOverviewScreen';
import { UgcTextScreen } from './templates/UgcTextScreen';
import { UgcPhotoGridScreen } from './templates/UgcPhotoGridScreen';
import { PlatformContext } from '../../providers/PlatformProvider';
import { bricksForCheckpoint } from './bricks/brick-registry';
import { StaticPage, Card, PressBtn } from '../../components/atoms';
import { G, P, T } from '../../styles/tokens';

const isMock = import.meta.env.VITE_FLOW_MOCK === 'true';
const { useFlow, useSubmitCheckpoint } = isMock ? mockClient : realClient;

function findLatestAttempt(data: GetFlowResponse, cpId: string): CheckpointAttempt | undefined {
  const forCp = data.attempts.filter((a) => a.checkpoint_id === cpId);
  if (forCp.length === 0) return undefined;
  return [...forCp].sort((a, b) => b.attempt_number - a.attempt_number)[0];
}

/** CTA label for TG MainButton mirroring in-page CtaBar. null = no CTA visible. */
function deriveCTALabel(data: GetFlowResponse | undefined, verdict: string | undefined): string | null {
  if (!data) return null;
  if (data.flow_run.status === 'completed') return 'На главную';
  if (!data.next_checkpoint) return null;
  if (verdict === 'pending') return null;
  if (verdict === 'rejected') return 'Переснять';
  const kind = data.next_checkpoint.kind;
  if (kind === 'MODERATION') return null;
  if (kind === 'PUBLISH') return 'Опубликовать';
  return 'Продолжить';
}

/** Render an honest inline error banner for 409/429 submit errors (EC-6, EC-7). */
function SubmitErrorBanner({ error }: { error: Error | null }) {
  if (!error) return null;
  const msg = error.message ?? '';
  let text: string;
  if (msg.startsWith('API 409')) {
    text = 'Достигнут лимит попыток. Обратитесь в поддержку.';
  } else if (msg.startsWith('API 429')) {
    text = 'Слишком много запросов. Подождите немного и попробуйте снова.';
  } else {
    text = 'Произошла ошибка. Попробуйте ещё раз.';
  }
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 80,
        left: 16,
        right: 16,
        padding: '12px 16px',
        borderRadius: 14,
        ...G.errorTag,
        zIndex: 50,
      }}
    >
      <p style={{ font: T.footnote, color: P.danger, margin: 0, textAlign: 'center' }}>{text}</p>
    </div>
  );
}

export default function FlowScreen() {
  const { slotId } = useParams<{ slotId: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useFlow(slotId ?? '');

  const cp = data?.next_checkpoint ?? null;
  const latestAttempt = cp ? findLatestAttempt(data!, cp.id) : undefined;
  const verdict = latestAttempt?.verdict;
  const ctaHandlerRef = useRef<(() => void) | null>(null);

  // Real submit hook — called unconditionally (hooks must not be conditional).
  // cpId falls back to '' when there is no active checkpoint (completed/no-cp states).
  const submitCheckpoint = useSubmitCheckpoint(slotId ?? '', cp?.id ?? '');

  // submitCheckpoint.error is automatically cleared by TanStack Query on the next mutate() call.
  const submitError = submitCheckpoint.error ?? null;

  // Back button: null-safe so tests without PlatformProvider render without throwing.
  const platform = useContext(PlatformContext);
  useEffect(() => {
    if (!platform) return;
    platform.showBackButton(() => navigate(-1));
    return () => { platform.hideBackButton(); };
  }, [platform, navigate]);

  // Haptics on verdict transition to rejected/accepted.
  useEffect(() => {
    if (verdict === 'rejected' || verdict === 'accepted') {
      platform?.hapticImpact('medium');
    }
  }, [verdict, platform]);

  // TG MainButton: mirror in-page CTA on Telegram platform only.
  const ctaLabel = deriveCTALabel(data, verdict);
  const nativeMainButton = platform?.capabilities.nativeBackButton ?? false;
  useEffect(() => {
    if (!platform || !nativeMainButton) return;
    if (!ctaLabel) { platform.hideMainButton(); return; }
    const handler = () => ctaHandlerRef.current?.();
    platform.setMainButton(ctaLabel, handler);
    return () => { platform.hideMainButton(); };
  }, [ctaLabel, nativeMainButton, platform]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100dvh', background: P.bg,
        }}
      >
        <div
          style={{
            width: 48, height: 48, borderRadius: 24,
            border: `4px solid rgba(226,114,91,0.18)`,
            borderTopColor: P.terracotta,
            animation: 'spin 1s linear infinite',
          }}
        />
        <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
          Загрузка...
        </span>
      </div>
    );
  }

  if (error || !data) {
    // BUG-1108: distinguish FLOW_NOT_FOUND (legacy slot, expected) from real errors.
    const status = (error as (Error & { status?: number }) | null)?.status;
    const msg = (error as Error | null)?.message ?? '';
    const isFlowNotFound = status === 404 && msg.includes('FLOW_NOT_FOUND');
    const text = isFlowNotFound
      ? 'Задание взято. Это задание выполняется в основном боте — вернись на главную.'
      : 'Не удалось загрузить задание. Попробуй открыть его заново или вернись на главную.';
    return (
      <StaticPage title="Задание" onBack={() => navigate(-1)}>
        <Card p={16} r={20}>
          <p style={{ margin: 0 }}>{text}</p>
          <div style={{ marginTop: 12 }}>
            <PressBtn onClick={() => navigate('/', { replace: true })}>
              На главную
            </PressBtn>
          </div>
        </Card>
      </StaticPage>
    );
  }

  // --- State machine ---

  /** Real onCta: submits the active checkpoint attempt (EC-5). */
  const onCta = () => {
    submitCheckpoint.mutate({ artefact_path: '' });
  };
  ctaHandlerRef.current = onCta;

  // 1. Completed flow → terminal done screen
  if (data.flow_run.status === 'completed') {
    const cashback = (data.flow_run as unknown as Record<string, unknown>).cashback_amount_kopecks;
    const handleDone = () => navigate('/');
    ctaHandlerRef.current = handleDone;
    return (
      <TerminalSheet
        variant="done"
        cashbackAmount={typeof cashback === 'number' ? Math.round(cashback / 100) : undefined}
        onCta={handleDone}
      />
    );
  }

  // 2. No next checkpoint on non-completed run → data error
  if (!data.next_checkpoint) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100dvh', background: P.bg,
        }}
      >
        <p style={{ font: T.footnote, color: P.muted, padding: '0 16px', textAlign: 'center', margin: 0 }}>
          Нет активных заданий
        </p>
      </div>
    );
  }

  const cpResolved = data.next_checkpoint;
  const commonProps = {
    checkpoint: cpResolved,
    attempt: latestAttempt,
    flowRun: data.flow_run,
    checkpoints: data.checkpoints,
    attempts: data.attempts,
    onCta,
  };

  // 3. Pending verdict → awaiting screen (no CTA)
  if (latestAttempt?.verdict === 'pending') return <AwaitingVerdict {...commonProps} />;

  // 4. Rejected → retry screen
  if (latestAttempt?.verdict === 'rejected') {
    return (
      <>
        <RejectScreen {...commonProps} />
        <SubmitErrorBanner error={submitError} />
      </>
    );
  }

  // 4a. Accepted → success + continue screen (EC-3)
  // Show accepted banner for the current checkpoint + the next checkpoint's instruction.
  if (latestAttempt?.verdict === 'accepted') {
    const nextInSequence = data.checkpoints
      .filter((c) => c.position === cpResolved.position + 1)
      .sort((a, b) => a.position - b.position)[0] ?? null;
    return (
      <S04SuccessContinue
        acceptedCheckpoint={cpResolved}
        totalCheckpoints={data.checkpoints.length}
        nextCheckpoint={nextInSequence}
        onCta={onCta}
      />
    );
  }

  // 5. Switch on checkpoint kind
  switch (cpResolved.kind) {
    case 'CART':
      return (
        <>
          <CartScreen {...commonProps} />
          <SubmitErrorBanner error={submitError} />
        </>
      );
    case 'PICKUP':
      return <PickupOverviewScreen {...commonProps} />;
    case 'UGC':
      return <UgcOverviewScreen {...commonProps} />;
    case 'UGC_TEXT':
      return <UgcTextScreen {...commonProps} />;
    case 'UGC_PHOTO':
      return (
        <>
          <UgcPhotoGridScreen {...commonProps} />
          <SubmitErrorBanner error={submitError} />
        </>
      );
    case 'MODERATION':
      return <ModerationStatus {...commonProps} />;
    case 'PHOTO':
    case 'VIDEO_UPLOAD':
    case 'PUBLISH': {
      // Brick dispatch: resolve bricks from artefact_requirements.
      const bricks = bricksForCheckpoint(cpResolved);
      if (bricks.length > 0) {
        // Render first brick (single brick per checkpoint step).
        // Multi-brick (url + screenshot for vk/tiktok) will advance via
        // separate checkpoints in the flow template.
        const { key, Component } = bricks[0];
        return (
          <>
            <Component key={key} {...commonProps} />
            <SubmitErrorBanner error={submitError} />
          </>
        );
      }
      // Fallback: no artefact_requirements -> InstructionScreen (WAIT-like steps)
      return (
        <>
          <InstructionScreen {...commonProps} />
          <SubmitErrorBanner error={submitError} />
        </>
      );
    }
    default:
      return (
        <>
          <InstructionScreen {...commonProps} />
          <SubmitErrorBanner error={submitError} />
        </>
      );
  }
}
